import { useCallback, useEffect, useRef, useState } from 'react';
import { fixWebmDuration } from '@fix-webm-duration/fix';
import { clearRecordedVideo, getRecordedVideo, saveRecordedVideo } from '../recording-indexeddb';

function sanitizeFileToken(value) {
  return String(value || 'exercise')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-');
}

function buildFileName(sessionId, timestamp) {
  const date = new Date(timestamp || Date.now());
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');

  return `${sanitizeFileToken(sessionId || 'session')}_${yyyy}-${mm}-${dd}_${hh}-${min}-${ss}.webm`;
}

export function createRecordingFileName(sessionId, timestamp) {
  return buildFileName(sessionId, timestamp);
}

export function getLatestRecordingPayload() {
  return getRecordedVideo();
}

export function clearLatestRecordingPayload() {
  return clearRecordedVideo();
}

function getActiveTracks(stream) {
  if (!stream || typeof stream.getTracks !== 'function') {
    return [];
  }

  return stream.getTracks().filter(track => track && track.readyState === 'live' && track.enabled);
}

function resolveRecordableStream(videoRef, mediaStream) {
  const videoElement = videoRef?.current?.video;
  const videoObjectStream = videoElement?.srcObject;
  const directStream = videoRef?.current?.stream;
  const capturedVideoStream =
    typeof videoElement?.captureStream === 'function'
      ? videoElement.captureStream()
      : typeof videoElement?.mozCaptureStream === 'function'
        ? videoElement.mozCaptureStream()
        : null;
  const candidates = [videoObjectStream, mediaStream, directStream, capturedVideoStream];

  return candidates.find(stream => getActiveTracks(stream).length > 0) ?? null;
}

const useRecordingVideo = ({
  videoRef,
  mediaStream,
  isCameraActive,
  shouldRecord,
  isPaused = false,
  videoRotation = 0,
  movementName,
  sessionId,
  recordingDurationSourceMs,
  setRecordingDate,
  shouldFinalizeRecording = false,
}) => {
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isStartRecording, setIsStartRecording] = useState(false);
  const [startDate, setStartDate] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingDurationMs, setRecordingDurationMs] = useState(0);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const startDateRef = useRef(0);
  const recordingMovementNameRef = useRef(movementName);
  const recordingSessionIdRef = useRef(sessionId);
  const recordingDurationRef = useRef(0);
  const rotationCanvasRef = useRef(null);
  const rotationCanvasStreamRef = useRef(null);
  const rotationAnimFrameRef = useRef(null);
  const videoRotationRef = useRef(videoRotation);

  useEffect(() => {
    videoRotationRef.current = videoRotation;
  }, [videoRotation]);

  useEffect(() => {
    recordingMovementNameRef.current = movementName;
  }, [movementName]);

  useEffect(() => {
    recordingSessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    recordingDurationRef.current = Number(recordingDurationSourceMs) || 0;
  }, [recordingDurationSourceMs]);

  const prepareRecorder = useCallback(() => {
    let stream = null;

    if (videoRotationRef.current && rotationCanvasStreamRef.current) {
      const canvasTracks = getActiveTracks(rotationCanvasStreamRef.current);
      if (canvasTracks.length > 0) {
        stream = rotationCanvasStreamRef.current;
      }
    }

    if (!stream) {
      stream = resolveRecordableStream(videoRef, mediaStream);
    }

    if (!stream) {
      return null;
    }

    const activeTracks = getActiveTracks(stream);
    if (activeTracks.length === 0) {
      return null;
    }

    const previousRecorder = mediaRecorderRef.current;
    if (
      (previousRecorder?.state === 'recording' || previousRecorder?.state === 'paused') &&
      streamRef.current === stream
    ) {
      return previousRecorder;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream, {
      mimeType: 'video/webm',
    });

    recorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = async () => {
      if (chunksRef.current.length === 0) {
        return;
      }

      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const stoppedAt = Date.now();
      const startedAt = startDateRef.current || stoppedAt;
      const durationMs = Math.max(0, Number(recordingDurationRef.current) || stoppedAt - startedAt);
      let finalBlob = blob;

      try {
        finalBlob = await fixWebmDuration(blob, durationMs, { logger: false });
      } catch (error) {
        console.error('Failed to inject duration into recorded video:', error);
      }

      const fileName = buildFileName(recordingSessionIdRef.current, startedAt);
      const payload = {
        blob: finalBlob,
        fileName,
        movementName: recordingMovementNameRef.current,
        sessionId: recordingSessionIdRef.current,
        startedAt,
        stoppedAt,
        durationMs,
      };
      saveRecordedVideo(payload).catch(error => {
        console.error('Failed to save recorded video to IndexedDB:', error);
      });
      setRecordedBlob(finalBlob);
      setRecordingDurationMs(durationMs);
      chunksRef.current = [];
    };

    mediaRecorderRef.current = recorder;
    setMediaRecorder(recorder);
    return recorder;
  }, [mediaStream, videoRef]);

  const startRecording = useCallback(() => {
    try {
      const recorder = prepareRecorder();
      if (!recorder) {
        return;
      }

      // A recorder paused by the effect below (camera/connection blip, or the
      // rest/upcoming isPaused prop) resumes in place — recreating it here
      // would start a brand new file and abandon the one already in progress.
      if (recorder.state === 'paused') {
        recorder.resume();
        return;
      }

      if (recorder.state === 'inactive') {
        setRecordedBlob(null);
        setIsStartRecording(false);
        setRecordingDurationMs(0);
        clearRecordedVideo().catch(error => {
          console.error('Failed to clear previous recorded video from IndexedDB:', error);
        });
        chunksRef.current = [];
        const now = Date.now();
        startDateRef.current = now;
        setIsStartRecording(true);
        setStartDate(now);
        if (setRecordingDate) {
          setRecordingDate(now);
        }
        recorder.start(250);
      }
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  }, [prepareRecorder, setRecordingDate]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') {
      try {
        recorder.pause();
      } catch (error) {
        console.error('Error pausing recording:', error);
      }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'paused') {
      try {
        recorder.resume();
      } catch (error) {
        console.error('Error resuming recording:', error);
      }
    }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      return;
    }

    try {
      if (recorder.state === 'recording' || recorder.state === 'paused') {
        setIsStartRecording(false);
        recorder.stop();
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
    }
  }, []);

  // Canvas-based rotation correction — must run BEFORE the startRecording effect
  // so the canvas stream is ready when prepareRecorder() is called.
  useEffect(() => {
    if (!videoRotation || !shouldRecord) {
      if (rotationAnimFrameRef.current) {
        cancelAnimationFrame(rotationAnimFrameRef.current);
        rotationAnimFrameRef.current = null;
      }
      return;
    }

    const videoEl = videoRef?.current?.video;
    if (!videoEl) return;

    if (!rotationCanvasRef.current) {
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;';
      document.body.appendChild(canvas);
      rotationCanvasRef.current = canvas;
    }

    const canvas = rotationCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const isSwapped = videoRotation === 90 || videoRotation === 270;
    const rad = (videoRotation * Math.PI) / 180;

    const applyFrame = () => {
      const sw = videoEl.videoWidth;
      const sh = videoEl.videoHeight;
      if (!sw || !sh) return;
      const cw = isSwapped ? sh : sw;
      const ch = isSwapped ? sw : sh;
      if (canvas.width !== cw) canvas.width = cw;
      if (canvas.height !== ch) canvas.height = ch;
      ctx.clearRect(0, 0, cw, ch);
      ctx.save();
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate(rad);
      ctx.drawImage(videoEl, -sw / 2, -sh / 2, sw, sh);
      ctx.restore();
    };

    // Draw one frame synchronously so the canvas has content before captureStream()
    applyFrame();

    // Create the canvas stream once — reused for subsequent recordings
    if (!rotationCanvasStreamRef.current) {
      rotationCanvasStreamRef.current = canvas.captureStream(30);
    }

    // Continuous drawing loop for the duration of the recording
    const loop = () => {
      applyFrame();
      rotationAnimFrameRef.current = requestAnimationFrame(loop);
    };
    rotationAnimFrameRef.current = requestAnimationFrame(loop);

    return () => {
      if (rotationAnimFrameRef.current) {
        cancelAnimationFrame(rotationAnimFrameRef.current);
        rotationAnimFrameRef.current = null;
      }
    };
  }, [videoRotation, shouldRecord, videoRef]);

  useEffect(() => {
    if (isPaused) {
      pauseRecording();
    } else {
      resumeRecording();
    }
  }, [isPaused, pauseRecording, resumeRecording]);

  useEffect(() => {
    // One recording per exercise: the caller flips shouldFinalizeRecording on
    // reaching comingup (between exercises) or post-performance (session end)
    // — either is a real finalize + save, not a pause. Anything else that can
    // make shouldRecord/isCameraActive go false for a beat (a WS reconnect
    // mid-exercise, a momentary camera flap) must not cut the current
    // exercise's clip short, so those just pause below instead.
    if (shouldFinalizeRecording) {
      stopRecording();
      return;
    }

    if (!isCameraActive) {
      stopRecording();
      return;
    }

    if (shouldRecord) {
      startRecording();
      return;
    }

    pauseRecording();
  }, [
    isCameraActive,
    shouldFinalizeRecording,
    mediaStream,
    pauseRecording,
    shouldRecord,
    startRecording,
    stopRecording,
  ]);

  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder?.state === 'recording' || recorder?.state === 'paused') {
        recorder.stop();
      }
      mediaRecorderRef.current = null;
      streamRef.current = null;
      chunksRef.current = [];

      if (rotationAnimFrameRef.current) {
        cancelAnimationFrame(rotationAnimFrameRef.current);
        rotationAnimFrameRef.current = null;
      }
      if (rotationCanvasStreamRef.current) {
        rotationCanvasStreamRef.current.getTracks().forEach(t => t.stop());
        rotationCanvasStreamRef.current = null;
      }
      if (rotationCanvasRef.current?.parentNode) {
        rotationCanvasRef.current.parentNode.removeChild(rotationCanvasRef.current);
        rotationCanvasRef.current = null;
      }
    };
  }, []);

  return {
    isStartRecording,
    startDate,
    mediaRecorder,
    startRecording,
    stopRecording,
    recordedBlob,
    recordingDurationMs,
  };
};

export default useRecordingVideo;
